// src/hooks/useLogin.ts
import { useState, useCallback } from 'react';
import config from '../config';
import { UserProfile } from '../interfaces';
import { useTranslation } from 'react-i18next';

interface LoginCredentials {
  login: string;
  password: string;
}

type AuthProvider = 'gitlab';

const TOKEN_STORAGE_KEY = 'auth_token';

const useLogin = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  // --- 1) Classic username/password (unchanged) ---
  const login = useCallback(async (credentials: LoginCredentials): Promise<UserProfile | null> => {
    setError(null);
    setLoading(true);
    try {
      const response = await fetch(`${config.centralApiBaseUrl}/session/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // Note: don't send 'Access-Control-Allow-Origin' from the client
        },
        body: JSON.stringify(credentials),
      });

      if (!response.ok) throw new Error(t("invalidUserNameOrPassword"));

      const data: UserProfile = await response.json();
      // If your backend returns a token with the profile, stash it for later calls
      const token = (data as any)?.token;
      if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);

      localStorage.setItem("isExternalLogin", "false");
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // --- 2) Start provider login (redirect to API -> GitLab) ---
  const loginWithProvider = useCallback((provider: AuthProvider, returnTo?: string) => {
    setError(null);
    const returnUrl = returnTo || `${config.centralFrontBaseUrl}/session/loginOAuth`; // your SPA route after auth
    const url = `${config.centralApiBaseUrl}/session/login/${provider}?returnUrl=${encodeURIComponent(returnUrl)}`;
    window.location.href = url; // full-page redirect
  }, []);

  // --- 3) Complete provider login on the SPA callback route ---
  // Call this inside your /auth/gitlab page (e.g., useEffect) AFTER the API redirected back with ?token=... or #token=...
  // in useLogin.ts
  const completeProviderLogin = useCallback(
    async ({ fetchProfile = true }: { fetchProfile?: boolean } = {}): Promise<UserProfile | null> => {
      setError(null);
      setLoading(true);
      try {
        const now = new URL(window.location.href);
        const tokenFromQuery = now.searchParams.get('token');
        const tokenFromHash = new URLSearchParams(now.hash.startsWith('#') ? now.hash.slice(1) : now.hash).get('token');
        const token = tokenFromQuery || tokenFromHash;
        if (!token) throw new Error('Missing token in callback URL');

        const isFirstLogin = (now.searchParams.get('isFirstLogin') || new URLSearchParams(now.hash.startsWith('#') ? now.hash.slice(1) : now.hash).get('isFirstLogin')) === 'true';
        if (isFirstLogin) {
          localStorage.setItem("isFirstLogin", "true");
        }

        localStorage.setItem(TOKEN_STORAGE_KEY, token);

        if (!fetchProfile) {
          // Optional: decode JWT for a minimal profile immediately
          try {
            const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
            return { // shape to match your UserProfile
              id: payload.sub ?? payload.nameid ?? 0,
              email: payload.email ?? '',
              name: payload.name ?? payload.unique_name ?? '',
              token
            } as any;
          } catch {
            return { token } as any;
          }
        }

        const resp = await fetch(`${config.centralApiBaseUrl}/session/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error('Failed to fetch profile');
        const profile: UserProfile = await resp.json();
        profile.token = token;
        localStorage.setItem("isExternalLogin", "true");
        return profile;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // --- Helpers ---
  const getToken = useCallback(() => localStorage.getItem(TOKEN_STORAGE_KEY), []);
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }, []);

  return {
    // password flow
    login,

    // provider flow
    loginWithProvider,         // call to start GitLab OAuth (redirect)
    completeProviderLogin,     // call on your SPA callback route to finish and get profile

    // utils
    getToken,
    logout,
    loading,
    error,
  };
};

export default useLogin;
export type { LoginCredentials, AuthProvider };
